/**
 * Chunk storage + world block access.
 *
 * A chunk is a full-height 16 x WORLD_HEIGHT x 16 column. Voxel data is stored
 * in flat typed arrays (SoA): Uint16 block ids and a packed Uint8 light array
 * (high nibble = skylight 0..15, low nibble = blocklight 0..15).
 *
 * Index layout: idx = x + z*CHUNK_SIZE + y*(CHUNK_SIZE*CHUNK_SIZE)
 * (y-major so vertical sunlight scans are cache friendly).
 *
 * Short chunks: because the layout is y-major, the open sky above a chunk's
 * terrain is the tail of both arrays, and the game server does not store it
 * (server/serverWorld.ts: about half of every chunk). A chunk's arrays may
 * therefore cover only the layers below `height`; every cell above is air in
 * full skylight with no block light. The accessors here, world/lighting.ts
 * and world/edit.ts honour that; other code that indexes `blocks` / `light`
 * directly must stay below `blocks.length`. The client's chunks are always
 * full height, where none of this changes anything.
 */

import { CHUNK_SIZE, WORLD_HEIGHT } from '../constants';
import { AIR, IS_OPAQUE, IS_SOLID, LIGHT_EMISSION, LIGHT_FILTER } from '../blocks';

export const CHUNK_AREA = CHUNK_SIZE * CHUNK_SIZE; // 256
export const CHUNK_VOLUME = CHUNK_AREA * WORLD_HEIGHT;
/**
 * Air layers a short chunk keeps above its highest block: block light (at most
 * 15) fades out inside them, so the light of every stored cell is exactly what
 * a full-height chunk would have.
 */
export const TOP_MARGIN = 16;
/** Packed light of a cell above a short chunk's arrays: skylight 15, block light 0. */
export const OPEN_SKY = 0xf0;

/** Layers a short copy of `blocks` needs: the highest non-air layer plus TOP_MARGIN, in steps of 16, at most WORLD_HEIGHT. */
export function shortHeight(blocks: Uint16Array): number {
  let i = Math.min(blocks.length, CHUNK_VOLUME) - 1;
  while (i >= 0 && blocks[i] === AIR) i--;
  return Math.min(WORLD_HEIGHT, (((i >> 8) + 1 + TOP_MARGIN + 15) >> 4) << 4);
}

export enum ChunkState {
  Empty = 0,     // not created
  Generating = 1, // queued in worker
  Generated = 2, // voxel data ready
  Lit = 3,       // sunlight/blocklight computed
  Ready = 4,     // mesh built & uploaded
}

export class Chunk {
  readonly cx: number;
  readonly cz: number;
  blocks: Uint16Array;
  light: Uint8Array;
  state: ChunkState = ChunkState.Generated;
  /** Set when lighting changed and the mesh needs a rebuild. */
  meshDirty = true;
  /** Number of non-air blocks — cheap "is this chunk empty" check. */
  solidCount = 0;
  lightDirty = false;

  constructor(cx: number, cz: number, blocks?: Uint16Array, light?: Uint8Array) {
    this.cx = cx;
    this.cz = cz;
    this.blocks = blocks ?? new Uint16Array(CHUNK_VOLUME);
    this.light = light ?? new Uint8Array(CHUNK_VOLUME);
  }

  /** Layers the arrays cover (WORLD_HEIGHT unless this is a short chunk). */
  get height(): number { return this.blocks.length / CHUNK_AREA; }

  /**
   * Make a short chunk's arrays cover layers 0..height-1 (rounded up to 16).
   * The arrays are replaced: do not hold on to `blocks` / `light` across an edit.
   */
  ensureHeight(height: number): void {
    const h = Math.min(WORLD_HEIGHT, ((height + 15) >> 4) << 4);
    const old = this.blocks.length;
    if (h * CHUNK_AREA <= old) return;
    const blocks = new Uint16Array(h * CHUNK_AREA);
    blocks.set(this.blocks);
    const light = new Uint8Array(h * CHUNK_AREA);
    light.set(this.light);
    light.fill(OPEN_SKY, old);
    this.blocks = blocks;
    this.light = light;
  }

  get(x: number, y: number, z: number): number {
    const i = x + z * CHUNK_SIZE + y * CHUNK_AREA;
    return i < this.blocks.length ? this.blocks[i] : AIR;
  }
  set(x: number, y: number, z: number, id: number): void {
    this.blocks[x + z * CHUNK_SIZE + y * CHUNK_AREA] = id;
  }
  sky(x: number, y: number, z: number): number {
    const i = x + z * CHUNK_SIZE + y * CHUNK_AREA;
    return i < this.light.length ? this.light[i] >> 4 : 15;
  }
  blockLight(x: number, y: number, z: number): number {
    const i = x + z * CHUNK_SIZE + y * CHUNK_AREA;
    return i < this.light.length ? this.light[i] & 0xf : 0;
  }
  setLight(x: number, y: number, z: number, sky: number, bl: number): void {
    this.light[x + z * CHUNK_SIZE + y * CHUNK_AREA] = (sky << 4) | bl;
  }
}

/**
 * Integer chunk key (no per-lookup string garbage). Valid for |cx|,|cz| < 32768.
 */
export function worldKey(cx: number, cz: number): number {
  return (cx + 32768) * 65536 + (cz + 32768);
}
export function keyCx(key: number): number {
  return Math.floor(key / 65536) - 32768;
}
export function keyCz(key: number): number {
  return (key % 65536) - 32768;
}

/** Bit flags for which of the 8 neighbors exist. */
export function neighborMask(world: World, cx: number, cz: number): number {
  let m = 0;
  let bit = 1;
  for (let dz = -1; dz <= 1; dz++) {
    for (let dx = -1; dx <= 1; dx++) {
      if (dx === 0 && dz === 0) continue;
      if (world.getChunk(cx + dx, cz + dz)) m |= bit;
      bit <<= 1;
    }
  }
  return m;
}

/**
 * The world: a sparse map of chunks plus global block/light accessors that
 * resolve across chunk borders. All coordinates are world coordinates.
 */
export class World {
  readonly chunks = new Map<number, Chunk>();
  readonly terrainSeed: number;

  constructor(seed: number) {
    this.terrainSeed = seed;
  }

  getChunk(cx: number, cz: number): Chunk | undefined {
    return this.chunks.get(worldKey(cx, cz));
  }

  setChunk(chunk: Chunk): void {
    this.chunks.set(worldKey(chunk.cx, chunk.cz), chunk);
  }

  removeChunk(cx: number, cz: number): void {
    this.chunks.delete(worldKey(cx, cz));
  }

  getBlock(wx: number, wy: number, wz: number): number {
    if (wy < 0) return 13; // below world: treat as bedrock (opaque)
    if (wy >= WORLD_HEIGHT) return AIR;
    const c = this.getChunk(wx >> 4, wz >> 4);
    if (!c) return AIR;
    const b = c.blocks;
    const i = (wx & 15) + (wz & 15) * CHUNK_SIZE + wy * CHUNK_AREA;
    return i < b.length ? b[i] : AIR; // above a short chunk's arrays: air
  }

  /** Light accessors; return skylight 15 above the world so the sky is lit. */
  getSky(wx: number, wy: number, wz: number): number {
    if (wy >= WORLD_HEIGHT) return 15;
    if (wy < 0) return 0;
    const c = this.getChunk(wx >> 4, wz >> 4);
    if (!c) return 15;
    const i = (wx & 15) + (wz & 15) * CHUNK_SIZE + wy * CHUNK_AREA;
    return i < c.light.length ? c.light[i] >> 4 : 15;
  }

  getBlockLight(wx: number, wy: number, wz: number): number {
    if (wy < 0 || wy >= WORLD_HEIGHT) return 0;
    const c = this.getChunk(wx >> 4, wz >> 4);
    if (!c) return 0;
    const i = (wx & 15) + (wz & 15) * CHUNK_SIZE + wy * CHUNK_AREA;
    return i < c.light.length ? c.light[i] & 0xf : 0;
  }

  isOpaque(wx: number, wy: number, wz: number): boolean {
    return IS_OPAQUE[this.getBlock(wx, wy, wz)] === 1;
  }

  isSolid(wx: number, wy: number, wz: number): boolean {
    return IS_SOLID[this.getBlock(wx, wy, wz)] === 1;
  }

  emission(wx: number, wy: number, wz: number): number {
    return LIGHT_EMISSION[this.getBlock(wx, wy, wz)];
  }

  filter(wx: number, wy: number, wz: number): number {
    return LIGHT_FILTER[this.getBlock(wx, wy, wz)];
  }

  /** Highest y with a solid block at column (wx,wz), or -1. */
  highestSolid(wx: number, wz: number): number {
    const c = this.getChunk(wx >> 4, wz >> 4);
    if (!c) return -1;
    const lx = wx & 15;
    const lz = wz & 15;
    for (let y = c.blocks.length / CHUNK_AREA - 1; y >= 0; y--) {
      if (IS_SOLID[c.blocks[lx + lz * CHUNK_SIZE + y * CHUNK_AREA]]) return y;
    }
    return -1;
  }
}
